import { AcceptElectricityAgreementOfferRequest, ChangeElectricityAgreementPackageRequest, CreateElectricityAgreementRequest, ElectricityAgreementPdfPreviewRequest, ElectricityPackage, ElectricityResaleAgreementOfferRequest } from './../../models/electricity/new-electricity-agreement';
import axios, { AxiosResponse } from "axios";
import { handleResponse, handleApiException, extractData, downloadFile, downloadFileWithPresignedUrl } from "helpers/apiResponseHelper";
import { apiService } from "services/api.service";
import { ElectricityAgreement, SimpleElectricityAgreement } from 'models/electricity/agreement';

async function getElectricityPackages(customerId: number, meteringPoints: string[]): Promise<ElectricityPackage[]> {
    return axios.get(apiService.getApiUrl() + '/electricity/packages/' + customerId + '?meteringPointEics=' + meteringPoints.join(','))
        .then(handleResponse).then(extractData).catch(handleApiException);
}

async function getElectricityAgreements(customerId: number): Promise<ElectricityAgreement[]> {
    return axios.get(apiService.getApiUrl() + '/electricity/agreements/' + customerId)
        .then(handleResponse).then(extractData).catch(handleApiException);
}

async function getPendingElectricityAgreements(customerId: number): Promise<SimpleElectricityAgreement[]> {
    return axios.get(apiService.getApiUrl() + '/electricity/agreements/' + customerId + '/pending')
        .then(handleResponse).then(extractData).catch(handleApiException);
}

async function getElectricityAgreementOffer(offerId: number, customerId: number): Promise<ElectricityAgreement> {
    return axios.get(`${apiService.getApiUrl()}/electricity/agreements/${customerId}/offer/${offerId}`)
        .then(handleResponse).then(extractData).catch(handleApiException);
}

async function postElectricityAgreement(customerId: number, request: CreateElectricityAgreementRequest): Promise<ElectricityAgreement> {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/' + customerId, request)
        .then(handleResponse).then(extractData).catch(handleApiException);
}

async function changeElectricityAgreementPackage(customerId: number, agreementId: number, request: ChangeElectricityAgreementPackageRequest, contactPersonId: number): Promise<ElectricityAgreement> {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/' + customerId + '/change-package/' + agreementId, request, {
        params: {
            contactPersonId
        }
    }).then(handleResponse).then(extractData).catch(handleApiException);
}

async function requestResaleAgreementOffer(customerId: number, request: ElectricityResaleAgreementOfferRequest): Promise<void> {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/' + customerId + '/resale-offer', request)
        .then(handleResponse).catch(handleApiException);
}

async function acceptAgreementOffer(customerId: number, agreementId: number, request: AcceptElectricityAgreementOfferRequest, contactPersonId: number): Promise<ElectricityAgreement> {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/' + customerId + '/accept/' + agreementId, request, {
        params: {
            contactPersonId
        }
    }).then(handleResponse).then(extractData).catch(handleApiException);
}

async function rejectAgreementOffer(customerId: number, agreementId: number, contactPersonId: number): Promise<ElectricityAgreement> {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/' + customerId + '/reject/' + agreementId, null, {
        params: {
            contactPersonId
        }
    }).then(handleResponse).then(extractData).catch(handleApiException);
}

async function getElectricityAgreementPdfPreview(request: ElectricityAgreementPdfPreviewRequest): Promise<Blob> {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/preview', request, { responseType: 'blob' }).then(handleResponse).catch(handleApiException);
}

async function getElectricityAgreementPdfPreviewWithDownload(request: ElectricityAgreementPdfPreviewRequest, fileName: string): Promise<void> {
    const url = apiService.getApiUrl() + '/electricity/agreements/preview';

    return getElectricityAgreementPdfPreview(request).then((res) => downloadFile(fileName, res, url));
}

async function getElectricityAgreementPdf(agreementId: number, filename?: string): Promise<AxiosResponse<string>> {        
    return axios.get(`${apiService.getApiUrl()}/electricity/agreements/${agreementId}/file?downloadAs=${filename}`)
        .then(handleResponse).catch(handleApiException);
}

async function getElectricityAgreementPdfWithDownload(agreementId: number, filename: string): Promise<void> {    
    return getElectricityAgreementPdf(agreementId, filename)
        .then((res) => downloadFileWithPresignedUrl(res.data));
}

async function getElectricityAgreementTermsPdf(packageId: number, isCompany: boolean): Promise<Blob> {    
    return axios.get(apiService.getApiUrl() + '/electricity/packages/' + packageId + '/file',
        {
            params: {isCompany},
            responseType: 'blob'
        }).then(handleResponse).catch(handleApiException);
}

const ElectricityAgreementService = {
    getElectricityPackages,
    getElectricityAgreements,
    getPendingElectricityAgreements,
    getElectricityAgreementOffer,
    postElectricityAgreement,
    getElectricityAgreementPdfPreview,
    getElectricityAgreementPdfPreviewWithDownload,
    getElectricityAgreementPdf,
    getElectricityAgreementPdfWithDownload,
    getElectricityAgreementTermsPdf,
    requestResaleAgreementOffer,
    changeElectricityAgreementPackage,
    acceptAgreementOffer,
    rejectAgreementOffer
};

export default ElectricityAgreementService;